import React from "react";
import useFetch from "react-fetch-hook";
import styled from "styled-components";
import css from "@styled-system/css";
import { grid, GridProps } from "styled-system";

import Box from "../Box/Box";
import { buildSections } from "./BCAFooterMenuBuilder";
import Typography from "../Typography/Typography";
import Logo from "../Logo/Logo";

export interface MenuProps {
  navigationLinksUrl: string;
}

export interface MenuSectionProps {
  id: string;
  label: string;
  children?: MenuItemProps[];
}

export interface MenuItemProps {
  id: string;
  openInNewWindow?: boolean;
  label: string;
  link: string;
  isVisible?: boolean;
}

const MenuSection = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  grid-column-start: span 3;
  @media only screen and (max-width: 1023px) {
    grid-column-start: span 4;
  }
  width: 100%;
  box-sizing: border-box;
`;

const FooterContainer = styled(Box)<GridProps>`
  grid-column-gap: 20px;
  grid-row-gap: 32px;
  ${grid}
`;

const LogoContainer = styled(Box)`
  grid-column-start: span 2;
  @media only screen and (max-width: 1023px) {
    grid-column-start: span 12;
  }
  @media only screen and (max-width: 414px) {
    grid-column-start: span 4;
  }
`;

const MenuItemLink = styled.a`
  ${css({
    letterSpacing: "normal",
    fontWeight: "normal",
    fontSize: "body",
    color: "text-color-over-background",
    textDecoration: "none",
    lineHeight: 1.7,
    "&:hover": {
      fontWeight: "semiBold",
      color: "text-color-hover",
      textDecoration: "underline",
    },
    "&:focus-within": {
      fontWeight: "semiBold",
      color: "text-color-hover",
      textDecoration: "underline",
    },
  })}
`;

const Anchor = styled.a`
  display: inline-block;
`;

const renderSections = (sections: MenuSectionProps[]) => {
  return sections.map((section: MenuSectionProps) => {
    return (
      <MenuSection key={section.label}>
        <Typography
          variant="body-medium"
          color="primary-white"
          mt={0}
          mx={0}
          mb={3}
          css={`
            letter-spacing: normal;
            line-height: normal;
            overflow: visible;
          `}
        >
          {section.label}
        </Typography>
        {section.children
          ?.filter((item) => item.isVisible)
          .map((item) => {
            return (
              <li key={item.label}>
                <MenuItemLink
                  href={item.link}
                  target={item.openInNewWindow ? "_blank" : "_top"}
                >
                  {item.label}
                </MenuItemLink>
              </li>
            );
          })}
      </MenuSection>
    );
  });
};

const LogoSection = () => (
  <LogoContainer
    width={["100%", "auto"]}
    display="flex"
    justifyContent={["center", "flex-start"]}
    aria-label="BCA logo container"
  >
    <Anchor
      href={"https://www.bca.co.uk/"}
      target={"_top"}
      rel="noopener noreferrer"
      aria-label="BCA Logo"
    >
      <Logo m={1}></Logo>
    </Anchor>
  </LogoContainer>
);

export default function BCAFooterMenu({ navigationLinksUrl }: MenuProps) {
  const { data, error } = useFetch(navigationLinksUrl);

  if (error) {
    return <LogoSection />;
  }

  const sections = buildSections(data);
  const sectionsComponents = renderSections(sections);

  return (
    <>
      {sectionsComponents.length > 0 ? (
        <FooterContainer
          color={"primary-white"}
          display="grid"
          pb={{ _: "48px", desktop: "56px" }}
          gridTemplateColumns={{
            tablet: "repeat(12, 1fr)",
            _: "repeat(4, 1fr)",
          }}
        >
          <LogoSection />

          {sectionsComponents}
        </FooterContainer>
      ) : (
        <LogoSection />
      )}
    </>
  );
}
